import React, { useState } from 'react';
import {
  Mail,
  CheckCircle,
  XCircle,
  Calendar,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminEmailTemplates } from '../../hooks/useAdminEmailTemplates';
import { formatDate } from '../../utils/dateUtils';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminEmailTemplates = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const {
    templates,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    toggleStatus,
    isToggling,
    deleteTemplate,
    isDeleting
  } = useAdminEmailTemplates();

  const columns = [
    {
      key: 'template_name',
      label: 'Template Name',
      sortable: true,
      width: 'w-48',
      render: (template) => (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
          {template.template_name || 'N/A'}
        </p>
      )
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      width: 'w-64',
      render: (template) => (
        <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
          {template.subject || 'N/A'}
        </p>
      )
    },
    {
      key: 'variables',
      label: 'Variables',
      sortable: false,
      width: 'w-48',
      render: (template) => (
        <div className="flex flex-wrap gap-1">
          {template.variables && Array.isArray(template.variables) && template.variables.length > 0 ? (
            template.variables.slice(0, 3).map((variable, index) => (
              <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                {variable}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">No variables</span>
          )}
          {template.variables && template.variables.length > 3 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">+{template.variables.length - 3} more</span>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (template) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {formatDate(template.created_at, 'medium')}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-24',
      render: (template) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          template.is_active
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
        }`}>
          {template.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (template) => {
        setEditingTemplate(template);
        setShowCreateModal(true);
      }
    },
    {
      label: template => template.is_active ? 'Deactivate' : 'Activate',
      icon: template => template.is_active ? ToggleLeft : ToggleRight,
      variant: 'default',
      onClick: (template) => {
        toggleStatus(template.id);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (template) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
          deleteTemplate(template.id);
        }
      }
    }
  ];

  const { totalTemplates, activeTemplates, inactiveTemplates } = statistics;

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Email Templates"
          subtitle="Manage email templates for automated communications"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Error Loading Email Templates</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {error?.response?.data?.message || 'Failed to load email templates. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Email Templates"
        subtitle="Manage email templates for automated communications"
        actions={
          <Button
            className="!rounded-xl flex items-center gap-2"
            onClick={() => {
              setEditingTemplate(null);
              setShowCreateModal(true);
            }}
          >
            <Plus className="w-4 h-4" />
            Create Template
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.emailTemplates.title}
        steps={ADMIN_PAGE_GUIDES.emailTemplates.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Templates</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{totalTemplates}</p>
            </div>
            <Mail className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 tabular-nums">{activeTemplates}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Inactive</p>
              <p className="text-2xl font-bold text-gray-600 dark:text-gray-300 mt-1 tabular-nums">{inactiveTemplates}</p>
            </div>
            <XCircle className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <SearchSelect
                label="Status"
                value={filters.is_active}
                onChange={(value) => handleFilterChange('is_active', value)}
                options={activeStatusOptions}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading email templates…</p>
            </div>
          ) : (
            <DataTable
              data={templates}
              columns={columns}
              actions={actions}
              isLoading={false}
              searchPlaceholder="Search templates by name, subject, or content..."
              onSearch={handleSearch}
              emptyMessage="No email templates found"
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
